<template>
    <div class="feature-rule">
        <!-- feature list picker -->
        <div
            :class="{ 'feature-undefined': rule.featureDeleted }"
            class="rule-editor--item">
            <pendo-multiselect
                :value="selectedFeature"
                :allow-empty="false"
                :options="featureListOptions"
                :searchable="true"
                :disabled="readOnly"
                :loading="loading"
                label-key="displayName"
                value-key="id"
                full-width
                placeholder="Select Feature"
                class="feature-rule--feature-select"
                @select="onFeatureSelect" />
            <rule-warning v-if="rule.featureDeleted">
                The original feature for this rule has been deleted
            </rule-warning>
        </div>
        <pendo-multiselect
            :value="rule.operator"
            :allow-empty="false"
            preselect-first
            full-width
            value-key="value"
            :options="operatorOptions"
            :disabled="!operatorOptions || readOnly"
            class="rule-editor--operator"
            @select="handleFeatureOperatorSelection" />
        <pendo-multiselect
            :value="featureTimeLabelObject(rule.time)"
            value-key="value"
            label-key="label"
            min-trigger-width="100%"
            :allow-empty="false"
            :preselect-first="true"
            :options="featureTimeRangeOptions"
            :disabled="readOnly"
            class="feature-rule--feature-time-operator"
            @select="handleFeatureTimeSelection" />
        <number-input
            v-if="showNumberInputComponent"
            class="rule-editor--item complex-rule number-input--max-height"
            :labels="{ suffix: 'times' }"
            :value="rule.value"
            :min="1"
            @input="$emit('input', $event)" />
        <component
            :is="timeComponent.component"
            v-if="showTimeComponent"
            v-bind="timeComponent.props"
            @input="$emit('input', $event)" />
    </div>
</template>

<script>
import RelativeDateRange from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-components/relative-date-range.vue';
import RuleWarning from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-components/rule-warning.vue';
import SingleDate from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-components/single-date.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-components/absolute-date-range.vue';
import NumberInput from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-components/number-input.vue';
import {
    SEGMENT_OPERATORS,
    FEATURE_TIME_RANGE_CLICKED_OPTIONS,
    FEATURE_TIME_RANGE_LAST_CLICKED_OPTIONS,
    FEATURE_TIME_RANGE_NOT_CLICKED_OPTIONS,
} from '@/segments/components/segment-editor-vue/adopt-segment-builder/constants/segments';
import { PendoMultiselect } from '@pendo/components';
import { getEntitiesByAppSelection } from '@/segments/components/segment-editor-vue/adopt-segment-builder/utils/utils';
import {
    getAbsoluteDateRangeConfig,
    getRelativeDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/adopt-segment-builder/utils/segments';
import ReadOnlyMixin from '@/segments/components/segment-editor-vue/adopt-segment-builder/rule-mixins/read-only.mixin';
import get from 'lodash/get';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'FeatureRule',
    components: {
        PendoMultiselect,
        AbsoluteDateRange,
        RelativeDateRange,
        RuleWarning,
        SingleDate,
        NumberInput,
        ResourceSelect,
    },
    mixins: [ReadOnlyMixin],
    props: {
        rule: {
            type: Object,
            required: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            loading: false,
        };
    },
    computed: {
        ...mapGetters({
            features: 'features/list',
        }),
        selectedFeature () {
            if (!this.rule?.feature) return null;

            return { ...this.rule.feature, displayName: this.rule.feature.trainingSettings?.displayName || this.rule.feature.name };
        },
        featureListOptions () {
            return getEntitiesByAppSelection(this.rule.app, this.features).map((feature) => ({
                ...feature,
                displayName: feature.trainingSettings?.displayName || feature.name,
            }));
        },
        operatorOptions () {
            return [...(SEGMENT_OPERATORS[this.rule.schema.schema] || [])];
        },
        featureTimeRangeOptions () {
            switch (this.rule.operator.value) {
                case 'lastclicked':
                    return FEATURE_TIME_RANGE_LAST_CLICKED_OPTIONS;
                case 'notclicked':
                    return FEATURE_TIME_RANGE_NOT_CLICKED_OPTIONS;
                case 'clicked':
                default:
                    return FEATURE_TIME_RANGE_CLICKED_OPTIONS;
            }
        },
        showNumberInputComponent () {
            return (
                this.rule.operator.value === 'clicked' && (this.rule.time === 'atmost' || this.rule.time === 'atleast')
            );
        },
        showTimeComponent () {
            if (this.showNumberInputComponent) {
                return false;
            }

            const { rule } = this;
            const operatorValue = get(rule, 'operator.value');

            return !!(rule.feature && operatorValue && rule.time && rule.time !== 'ever');
        },
        timeComponent () {
            if (['withinlast', '!withinlast'].includes(this.rule.time)) {
                return getRelativeDateRangeConfig(this.rule, 'value');
            }

            if (['between'].includes(this.rule.time)) {
                return getAbsoluteDateRangeConfig(this.rule);
            }

            if (['before', 'since'].includes(this.rule.time)) {
                return getSingleDateConfig(this.rule, 'value');
            }

            return {};

        },
        // determine if we should show the time selector
        // eslint-disable-next-line internal-rules/no-unused-properties
        showTimeSelector () {
            const { rule } = this;

            return !!rule.feature;
        },
    },
    created () {
        this.fetchFeatures();
    },
    methods: {
        ...mapActions({
            loadAllFeatures: 'features/loadAll',
        }),
        async fetchFeatures () {
            try {
                this.loading = true;
                await this.loadAllFeatures();
            } catch (error) {
                console.error('Error fetching features:', error);
            } finally {
                this.loading = false;
            }
        },
        featureTimeLabelObject (timeString) {
            return this.featureTimeRangeOptions.find((opt) => opt.value === timeString);
        },
        handleFeatureOperatorSelection ($event) {
            const updates = [{ field: 'operator', value: $event }];

            // handle switching to operator where `at least / at most` not supported
            if (this.rule.time === 'atleast' || this.rule.time === 'atmost') {
                if ($event.value === 'notclicked') {
                    updates.push({ field: 'time', value: 'ever' });
                }

                if ($event.value === 'lastclicked') {
                    updates.push({ field: 'time', value: 'before' });
                }
            }

            this.$emit('input', updates);
        },
        handleFeatureTimeSelection ($event) {
            const { value } = $event;

            if (value === 'withinlast' || value === '!withinlast') {
                this.$emit('input', { field: 'value', value: { count: 30, granularity: 'days' } });
            } else if (value !== 'before' && value !== 'since') {
                this.$emit('input', { field: 'value', value: 1 });
            }

            this.$emit('input', { field: 'time', value });
        },
        onFeatureSelect (feature) {
            this.$emit('input', [
                { field: 'feature', value: feature },
                { field: 'featureDeleted', value: false },
            ]);
        },
    },
};
</script>

<style lang="scss" scoped>
.feature-rule {
    display: contents;

    .feature-undefined {
        :deep(.pendo-multiselect__trigger) {
            // $red-error = #db1111 which does not exist in our variables
            border: 1px solid var(--palette-red-50);
        }
    }
}

.number-input {
    &--max-height {
        height: 36px;
    }
}
</style>
